import React, {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useMemo,
  useReducer,
} from "react";
import { getPortals } from "../services/api/apiClient";
import {
  Language,
  PortalPageVisibility,
  PortalResultType,
} from "../services/api/types/portalConfiguration/portal";
import { useAuth } from "./AuthContext";

type LoadStatus = "idle" | "loading" | "loaded" | "error";

const selectionStorageKeys = [
  "selectedUsers",
  "selectedUsersIds",
  "manualSelectedUsers",
  "manualSelectedUsersIds",
  "filteredSelectedUsers",
  "filteredSelectedUsersIds",
  "filteredUserSelections",
  "selectedUsersSource",
  "fileHubSelectedFiles",
  "fileHubAppliedFiltersList",
];

const clearSelectionStorageKeys = () => {
  selectionStorageKeys.forEach((key) => localStorage.removeItem(key));
};

export type DropdownPortal = {
  portalId: number;
  portalUid: string;
  name: string;
  isSubPortal: boolean;
  authoringTool: boolean;
  isMain: boolean;
  customTranslation: boolean;
  portalLanguages: Language[];
  owners: Owners[];
  portalPages?: PortalPageVisibility[];
};

export type Owners = {
  portalUid: string;
  name: string;
};

type State = {
  portals: DropdownPortal[];
  selectedPortal: DropdownPortal | null;
  loadStatus: LoadStatus;
};

type Action =
  | { type: "SET_PORTALS"; payload: DropdownPortal[] }
  | { type: "SET_SELECTED_PORTAL"; payload: DropdownPortal }
  | { type: "CLEAR_SELECTED_PORTAL" }
  | {
      type: "SET_LOAD_STATUS";
      payload: LoadStatus;
    };

const initialState: State = {
  portals: [],
  selectedPortal: null,
  loadStatus: "idle",
};

const PortalContext = createContext<{
  portals: State;
  dispatch: React.Dispatch<Action>;
  fetchPortals: () => void;
}>({
  portals: initialState,
  dispatch: () => null,
  fetchPortals: () => {},
});

const portalReducer = (state: State, action: Action): State => {
  switch (action.type) {
    case "SET_PORTALS":
      return { ...state, portals: action.payload };
    case "SET_SELECTED_PORTAL":
      localStorage.setItem("selectedPortal", action.payload.portalUid);
      localStorage.setItem("selectedPortalName", action.payload.name);
      localStorage.setItem(
        "selectedOwnersFilter",
        JSON.stringify(action.payload.owners),
      );
      if (
        state.selectedPortal &&
        state.selectedPortal.portalUid !== action.payload.portalUid
      ) {
        clearSelectionStorageKeys();
      }
      return { ...state, selectedPortal: action.payload };
    case "CLEAR_SELECTED_PORTAL":
      clearSelectionStorageKeys();
      return { ...state, selectedPortal: null };
    case "SET_LOAD_STATUS": {
      return {
        ...state,
        loadStatus: action.payload,
      };
    }
    default:
      return state;
  }
};

export const PortalProvider = ({ children }: { children: ReactNode }) => {
  const [portals, dispatch] = useReducer(portalReducer, initialState);
  const { auth } = useAuth();

  const fetchPortals = useCallback(async () => {
    try {
      dispatch({
        type: "SET_LOAD_STATUS",
        payload: "loading",
      });
      const response = await getPortals({
        resultType: PortalResultType.DDL,
      });
      if (response) {
        dispatch({ type: "SET_PORTALS", payload: response });
        const selectedPortalUid = localStorage.getItem("selectedPortal");
        if (selectedPortalUid) {
          const portal = response.find(
            (p: DropdownPortal) => p.portalUid === selectedPortalUid,
          );
          if (portal) {
            dispatch({ type: "SET_SELECTED_PORTAL", payload: portal });
          }
        } else {
          const portal = response.find(
            (p: DropdownPortal) => p.portalUid === auth.user?.PortalUid,
          );
          if (portal) {
            dispatch({ type: "SET_SELECTED_PORTAL", payload: portal });
          }
        }
      }
    } catch (error) {
      console.log("ERROR: ", error);
    } finally {
      dispatch({
        type: "SET_LOAD_STATUS",
        payload: "loaded",
      });
    }
  }, [auth.user]);

  const memoizedValue = useMemo(
    () => ({ portals, dispatch, fetchPortals }),
    [portals, dispatch, fetchPortals],
  );

  return (
    <PortalContext.Provider value={memoizedValue}>
      {children}
    </PortalContext.Provider>
  );
};

export const usePortal = () => useContext(PortalContext);
